/**
 * fetchApi — Likkan System PHP backend client.
 *
 * The backend is reached via the Vite dev proxy (see vite.config.js), so
 * relative URLs like '/api/v1/user/login' just work in development.
 * For production, deploy the React build behind the same Apache vhost
 * that serves /api, or change BASE_URL below.
 *
 * Cookies: the session is stored in a single cookie by the backend
 * (`Likkan-System-Session-Token`). `credentials: 'include'` makes the
 * browser send + accept the cookie on every request.
 */

const API_KEY =
  import.meta.env.VITE_LIKKAN_API_KEY ||
  (typeof __LIKKAN_API_KEY__ !== 'undefined' ? __LIKKAN_API_KEY__ : '__set_me_in_env__');

// In dev, empty BASE_URL + proxy means call /api/v1/... and Vite forwards.
// In production, set VITE_API_BASE_URL to e.g. 'https://likkan.example.com'.
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (typeof __LIKKAN_API_BASE__ !== 'undefined' ? __LIKKAN_API_BASE__ : '');

const fetchApi = async (url, body = {}, method = 'POST', headers = {}) => {
  const isAbsolute = /^https?:\/\//.test(url);
  const fullUrl = isAbsolute ? url : `${BASE_URL}${url}`;

  return fetch(fullUrl, {
    method,
    credentials: 'include',                // send + accept session cookie
    body: method === 'GET' ? undefined : JSON.stringify(body),
    headers: new Headers({
      ...headers,
      'Content-Type': 'application/json',
      'Likkan-API-KEY': API_KEY,
    }),
  })
    .then((response) => response.json())
    .catch((error) => {
      console.error('fetchApi error', error);
      return { state: 0, error: ['network_error'], http_status: 0 };
    });
};

/**
 * fetchDownload — for binary content (e.g. PDF).
 *
 * We can't use window.open() because the browser would navigate as a
 * plain GET request, which means no Likkan-API-KEY header reaches the
 * backend (and it 4xx's with `{"error":"invalid_api_key"}`). Fetching
 * directly attaches the header + session cookie, then we trigger a
 * download via a temporary <a download>.
 *
 * Returns { ok: boolean, error?: string }.
 */
export const fetchDownload = async (url, filename) => {
  const isAbsolute = /^https?:\/\//.test(url);
  const fullUrl = isAbsolute ? url : `${BASE_URL}${url}`;

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      credentials: 'include',
      headers: new Headers({
        'Likkan-API-KEY': API_KEY,
      }),
    });
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        detail = typeof err.error === 'string' ? err.error : (Array.isArray(err.error) ? err.error.join(', ') : detail);
      } catch (_) { /* keep the status line */ }
      return { ok: false, error: detail };
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
    return { ok: true };
  } catch (error) {
    console.error('fetchDownload error', error);
    return { ok: false, error: error?.message || 'network_error' };
  }
};

export default fetchApi;
